import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import EllipsisMenu       from 'app/components/common/ellipsis-menu';
import Icon               from 'app/components/common/icon';
import Link               from 'app/components/common/link';
import Meta               from 'app/components/common/meta';
import ModalNylasGrant    from 'app/components/company-admin/integrations/modal-nylas-grant';
import CadminLayout       from 'app/components/company-admin/layout/';
import PageLoading        from 'app/components/layout/page-loading';
import IntsDuck           from 'app/ducks/company-admin/integrations';
import paths              from 'app/paths';
import prompts            from 'app/prompts';
import AuthSlx            from 'app/selectors/auth';
import CadminSlx          from 'app/selectors/company-admin/';


const getProviderLabel = (ng) => {
  if (ng.provider === 'google') return 'Google Calendar';
  if (ng.provider === 'microsoft') return 'Outlook';
  return ng.provider;
};
const getIsValid = ng => ng.status === 'valid';

class CadminPageIntegrationsCalendar extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      manageNgId: null,
    };

    this.onClickConnectAccountGoogle = this.onClickConnectAccountGoogle.bind(this);
    this.onClickConnectAccountMs = this.onClickConnectAccountMs.bind(this);
    this.onClickManageCalendars = this.onClickManageCalendars.bind(this);
    this.onCloseManageCalendars = this.onCloseManageCalendars.bind(this);
    this.onClickDisconnect = this.onClickDisconnect.bind(this);
  }

  async onClickConnectAccount(provider) {
    const properName = ({
      google: 'Google',
      microsoft: 'Microsoft',
    })[provider];
    const msg = `The calendar integration requires connecting a single "integration user". Events created in Millie will be added to this user's calendar. We recommend creating a "Millie" user in ${properName}.\nThe process that follows entails signing into ${properName} as that user. Please don't complete the process with your personal ${properName} account.`;
    const didConfirm = await prompts.confirm({msg, title: 'Before Proceeding...', confirmBtnLabel: 'Proceed', doubleConfirm: true});
    if (didConfirm) {
      window.location.href = paths.cadminRedirectConnectCalendar(this.props.company.slug, {provider});
    }
  }
  async onClickConnectAccountGoogle() {
    return this.onClickConnectAccount('google');
  }
  async onClickConnectAccountMs() {
    return this.onClickConnectAccount('microsoft');
  }

  onClickManageCalendars(ng, event) {
    event.preventDefault();
    this.setState({manageNgId: ng.id});
  }
  onCloseManageCalendars() {
    this.setState({manageNgId: null});
  }

  async onClickDisconnect(ng, event) {
    event.preventDefault();
    const msg = `This will remove Millie's ability to keep events and participants in sync with any calendars on this account.\nEven if this account is reconnected, Millie will not be able to reconnect to events it previously managed.\nIf you're trying to reestablish the connection for this account, please use the "Reauthenticate" option instead.\nDisconnecting this account will not delete any of the events created by Millie.`;
    const didConfirm = await prompts.confirm({msg, danger: 'Disconnect Account', doubleConfirm: true});
    if (didConfirm) {
      this.props.destroyGrant(ng.id);
    }
  }

  renderManageModal() {
    const {grants, company} = this.props;
    const nylasGrant = (grants || []).find(ng => ng.id === this.state.manageNgId);
    if (!nylasGrant) return null;
    return <ModalNylasGrant nylasGrant={nylasGrant} onClose={this.onCloseManageCalendars} company={company} />;
  }

  renderGrants() {
    const {company, grants, calendars, destroyingId, isBsSuper} = this.props;
    if (!grants) return 'loading...';
    if (!grants.length) return 'Click Connect above to get started.';

    return (<>
      {grants.map((ng) => {
        const isValid = getIsValid(ng);
        const isDestroying = destroyingId === ng.id;
        const grantCals = calendars.filter((cal) => {
          if (cal.readOnly) return false;
          if (cal.disabled) return false;
          return cal.nylasGrantId === ng.id;
        });

        return (
          <div className={`ca-box ${isDestroying ? '_opac-50' : ''}`} key={ng.id}>
            <div className="ca-box-header">
              <h1 className="ca-box-header-title _flex _gap-12">{getProviderLabel(ng)} <span className={`chip ${isValid ? 'green' : 'pink'}`}>{isValid ? 'Connected' : 'Expired'}</span></h1>
              <div className="ca-box-header-controls">
                <EllipsisMenu icon={<Icon.Cog1 />}>
                  <a href="#" onClick={this.onClickManageCalendars.bind(this, ng)}>Manage Calendars</a>
                  {!isValid && <a href={paths.cadminRedirectConnectCalendar(company.slug, {provider: ng.provider})}>Reauthenticate</a>}
                  <a href="#" onClick={this.onClickDisconnect.bind(this, ng)}>Disconnect account</a>
                </EllipsisMenu>
              </div>
            </div>
            <div className="ca-box-body">
              <table className="attrs">
                <tbody>
                  <tr>
                    <th>Integration User</th>
                    <td>{ng.email}</td>
                  </tr>
                  <tr>
                    <th>Enabled Calendars</th>
                    <td>
                      <div className="_flex _col _align-start _justify-start _gap-6">
                        {grantCals.map((cal) => {
                          const isDefault = company.defaultNylasCalendarId === cal.id;
                          const isDeleted = !!cal.deleted;
                          return (
                            <div key={cal.id} className="_flex _align-center _justify-start _gap-6">
                              <span>{cal.title}</span>
                              {isDefault && <span className="chip small blue">Default</span>}
                              {isDeleted && <span className="chip small danger">Deleted</span>}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>);
  }

  render() {
    const {company, grants, calendars, destroyingId, isBsSuper} = this.props;
    if (!company) return <PageLoading />;

    const showConnectBtns = (() => {
      if (isBsSuper) return true;
      if ((grants || []).length) return false;
      return true;
    })();

    return (
      <CadminLayout company={company} activeItem="integrations">
        <Meta title={`Calendar Integration | Millie`} />

        <div className="ca-main-head">
          <h1 className="ca-main-head-h1">Integrations</h1>
          <div className="ca-main-head-actions">
            {showConnectBtns && (<>
              <button className="btn blue" onClick={this.onClickConnectAccountGoogle}>Connect Google Calendar</button>
              <button className="btn blue" onClick={this.onClickConnectAccountMs}>Connect Microsoft Outlook</button>
            </>)}
          </div>
        </div>

        <div className="ca-main-tabs">
          <Link href={paths.cadminSlackSettings(company.slug)} className="ca-main-tabs-tab">Slack</Link>
          <Link href={paths.cadminIntsCalendar(company.slug)}  className="ca-main-tabs-tab active">Calendar</Link>
        </div>

        {this.renderGrants()}
        {this.renderManageModal()}
      </CadminLayout>
    );
  }

}

const stateToProps = (state) => ({
  company: CadminSlx.company(state),
  isBsSuper: AuthSlx.isBsSuper(state),
  grants: IntsDuck.Slx.nylasGrants(state),
  calendars: IntsDuck.Slx.nylasCalendars(state),
  destroyingId: IntsDuck.Slx.nylasGrantsDestroyPendingId(state),
});
const dispatchToProps = (dispatch) => ({
  destroyGrant: (grantId) => dispatch(IntsDuck.Ax.grantsDestroy(undefined, grantId)),
});

export default connect(stateToProps, dispatchToProps)(CadminPageIntegrationsCalendar);
